import React, { useState } from 'react';

const Kundli_Details_View = ({ data, formData, onReset }) => {
    const [activeTab, setActiveTab] = useState('planets');

    if (!data) return null;

    const { planets = [], houses = [], ascendant, midheaven, vertex, lilith, aspects = [] } = data;

    // Helper for aspect badge colors
    const getAspectBadgeClass = (type) => {
        switch (type?.toLowerCase()) {
            case 'conjunction':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'trine':
                return 'bg-emerald-100 text-emerald-800 border-emerald-200';
            case 'sextile':
                return 'bg-sky-100 text-sky-800 border-sky-200';
            case 'square':
                return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'opposition':
            case 'opposite':
                return 'bg-rose-100 text-rose-800 border-rose-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    // Helper to get planet badge colors
    const getPlanetBadge = (name) => {
        const colors = {
            Sun: 'bg-amber-500 text-white',
            Moon: 'bg-slate-500 text-white',
            Mars: 'bg-red-600 text-white',
            Mercury: 'bg-emerald-600 text-white',
            Jupiter: 'bg-yellow-600 text-white',
            Venus: 'bg-pink-500 text-white',
            Saturn: 'bg-indigo-700 text-white',
            Uranus: 'bg-cyan-600 text-white',
            Neptune: 'bg-blue-600 text-white',
            Pluto: 'bg-purple-700 text-white',
            Node: 'bg-teal-700 text-white',
            Chiron: 'bg-orange-600 text-white',
            'Part of Fortune': 'bg-amber-600 text-white',
        };
        return colors[name] || 'bg-gray-600 text-white';
    };

    return (
        <div className="w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mt-8 transition-all duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-[#9F2225] via-[#831c1e] to-[#631416] p-6 text-white relative overflow-hidden">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
                    <div>
                        <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md text-xs font-semibold rounded-full uppercase tracking-wider mb-2">
                            Western Horoscope Chart
                        </span>
                        <h2 className="text-2xl md:text-3xl font-bold">
                            {formData?.name ? `${formData.name}'s Kundli Analysis` : 'Horoscope & Kundli Details'}
                        </h2>
                        {formData?.birthPlace && (
                            <p className="text-white/80 text-sm mt-1">
                                Born at {formData.birthPlace} {formData.birthDateTime && `• ${new Date(formData.birthDateTime).toLocaleString()}`}
                            </p>
                        )}
                    </div>
                    {onReset && (
                        <button
                            onClick={onReset}
                            className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white text-xs font-semibold rounded-lg border border-white/30 transition-all duration-200 flex items-center gap-2"
                        >
                            <span>↺</span> Calculate Another
                        </button>
                    )}
                </div>
            </div>

            {/* Key Angles Cards */}
            <div className="p-6 bg-gray-50/50 border-b border-gray-100">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">Key Astronomical Points</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                        <div className="text-xs text-gray-500 font-medium">Ascendant (Lagna)</div>
                        <div className="text-lg font-bold text-[#9F2225] mt-1">
                            {typeof ascendant === 'number' ? `${ascendant.toFixed(2)}°` : ascendant || 'N/A'}
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                        <div className="text-xs text-gray-500 font-medium">Midheaven (MC)</div>
                        <div className="text-lg font-bold text-[#9F2225] mt-1">
                            {typeof midheaven === 'number' ? `${midheaven.toFixed(2)}°` : midheaven || 'N/A'}
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                        <div className="text-xs text-gray-500 font-medium">Vertex</div>
                        <div className="text-lg font-bold text-[#9F2225] mt-1">
                            {typeof vertex === 'number' ? `${vertex.toFixed(2)}°` : vertex || 'N/A'}
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                        <div className="text-xs text-gray-500 font-medium">Lilith</div>
                        <div className="text-lg font-bold text-[#9F2225] mt-1">
                            {lilith?.sign || 'N/A'} {lilith?.norm_degree != null ? `(${lilith.norm_degree.toFixed(2)}°)` : ''}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-0.5">House {lilith?.house ?? 'N/A'}</div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex border-b border-gray-200 px-6 bg-white overflow-x-auto">
                <button
                    onClick={() => setActiveTab('planets')}
                    className={`py-4 px-5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors duration-200 ${
                        activeTab === 'planets'
                            ? 'border-[#9F2225] text-[#9F2225]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    🪐 Planetary Positions ({planets.length})
                </button>
                <button
                    onClick={() => setActiveTab('houses')}
                    className={`py-4 px-5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors duration-200 ${
                        activeTab === 'houses'
                            ? 'border-[#9F2225] text-[#9F2225]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    🏠 Houses / Bhavas ({houses.length})
                </button>
                <button
                    onClick={() => setActiveTab('aspects')}
                    className={`py-4 px-5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors duration-200 ${
                        activeTab === 'aspects'
                            ? 'border-[#9F2225] text-[#9F2225]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    ⚡ Planetary Aspects ({aspects.length})
                </button>
            </div>

            {/* Tab Content */}
            <div className="p-6">
                {/* PLANETS TAB */}
                {activeTab === 'planets' && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-50/50">
                                    <th className="py-3 px-4">Planet</th>
                                    <th className="py-3 px-4">Zodiac Sign</th>
                                    <th className="py-3 px-4">House</th>
                                    <th className="py-3 px-4">Degree (Norm)</th>
                                    <th className="py-3 px-4">Full Degree</th>
                                    <th className="py-3 px-4">Speed</th>
                                    <th className="py-3 px-4">Motion</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {planets.map((planet, idx) => {
                                    const isRetro = planet.is_retro === 'true' || planet.is_retro === true;
                                    return (
                                        <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                                            <td className="py-3.5 px-4 font-semibold text-gray-900 flex items-center gap-2">
                                                <span className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${getPlanetBadge(planet.name)}`}>
                                                    {planet.name ? planet.name.slice(0, 2) : 'P'}
                                                </span>
                                                {planet.name}
                                            </td>
                                            <td className="py-3.5 px-4">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                                                    {planet.sign}
                                                </span>
                                            </td>
                                            <td className="py-3.5 px-4 font-medium text-gray-700">House {planet.house}</td>
                                            <td className="py-3.5 px-4 text-gray-600 font-mono">
                                                {typeof planet.norm_degree === 'number' ? `${planet.norm_degree.toFixed(2)}°` : planet.norm_degree}
                                            </td>
                                            <td className="py-3.5 px-4 text-gray-500 font-mono text-xs">
                                                {typeof planet.full_degree === 'number' ? `${planet.full_degree.toFixed(2)}°` : planet.full_degree}
                                            </td>
                                            <td className="py-3.5 px-4 text-gray-600 font-mono text-xs">
                                                {typeof planet.speed === 'number' ? planet.speed.toFixed(4) : planet.speed}
                                            </td>
                                            <td className="py-3.5 px-4">
                                                {isRetro ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                                                        Retrograde (R)
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                        Direct
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* HOUSES TAB */}
                {activeTab === 'houses' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {houses.map((h, idx) => {
                            const housePlanets = planets.filter((p) => p.house === h.house);
                            const hasLilith = lilith && lilith.house === h.house;
                            return (
                                <div key={idx} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:border-[#9F2225]/40 hover:shadow-md transition-all">
                                    <div className="flex justify-between items-center pb-2 border-b border-gray-100 mb-3">
                                        <span className="text-xs font-bold uppercase tracking-wider text-[#9F2225]">
                                            House {h.house} {h.house === 1 && '(Ascendant)'}
                                        </span>
                                        <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                            {typeof h.degree === 'number' ? `${h.degree.toFixed(2)}°` : h.degree}
                                        </span>
                                    </div>
                                    <div className="text-base font-bold text-gray-800 mb-2">{h.sign}</div>
                                    <div className="mt-3 pt-2 border-t border-dashed border-gray-100">
                                        <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                                            Planets Present
                                        </div>
                                        {housePlanets.length === 0 && !hasLilith ? (
                                            <span className="text-xs text-gray-400 italic">No major planets</span>
                                        ) : (
                                            <div className="flex flex-wrap gap-1.5">
                                                {housePlanets.map((p, pIdx) => (
                                                    <span
                                                        key={pIdx}
                                                        className={`px-2 py-0.5 rounded text-xs font-semibold ${getPlanetBadge(p.name)}`}
                                                    >
                                                        {p.name}
                                                    </span>
                                                ))}
                                                {hasLilith && (
                                                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#9F2225] text-white">
                                                        Lilith
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ASPECTS TAB */}
                {activeTab === 'aspects' && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-50/50">
                                    <th className="py-3 px-4">Aspecting Planet</th>
                                    <th className="py-3 px-4">Aspect Type</th>
                                    <th className="py-3 px-4">Aspected Planet / Point</th>
                                    <th className="py-3 px-4">Orb</th>
                                    <th className="py-3 px-4">Difference</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {aspects.map((aspect, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                                        <td className="py-3.5 px-4 font-semibold text-gray-900 flex items-center gap-2">
                                            <span className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${getPlanetBadge(aspect.aspecting_planet)}`}>
                                                {aspect.aspecting_planet ? aspect.aspecting_planet.slice(0, 2) : 'P'}
                                            </span>
                                            {aspect.aspecting_planet}
                                        </td>
                                        <td className="py-3.5 px-4">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${getAspectBadgeClass(aspect.type)}`}>
                                                {aspect.type}
                                            </span>
                                        </td>
                                        <td className="py-3.5 px-4 font-semibold text-gray-800 flex items-center gap-2">
                                            <span className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${getPlanetBadge(aspect.aspected_planet)}`}>
                                                {aspect.aspected_planet ? aspect.aspected_planet.slice(0, 2) : 'P'}
                                            </span>
                                            {aspect.aspected_planet}
                                        </td>
                                        <td className="py-3.5 px-4 text-gray-600 font-mono text-xs">
                                            {typeof aspect.orb === 'number' ? `${aspect.orb.toFixed(2)}°` : aspect.orb}
                                        </td>
                                        <td className="py-3.5 px-4 text-gray-500 font-mono text-xs">
                                            {typeof aspect.diff === 'number' ? `${aspect.diff.toFixed(2)}°` : aspect.diff}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Kundli_Details_View;
